import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import styles from './FilterComponent.module.scss';
import MerchantSelectionComponent from '../MerchantSelectionComponent/MerchantSelectionComponent';
import boddVariables from '../../../../variables.module.scss';
import { SpreadsheetIcon } from '../IconSelectorComponent/IconSelectorComponent';
import CategoryTagFilterComponent from '../CategoryTagFilterComponent/CategoryTagFilterComponent';
import SessionTagFilterComponent from '../SessionTagFilterComponent/SessionTagFilterComponent';
import GarmentTypeFilterComponent from '../GarmentTypeFilterComponent/GarmentTypeFilterComponent';
import TextFilterComponent from '../TextFilterComponent/TextFilterComponent';
import { useAppSelector } from '../../../../app/hooks';
import { selectIsBoddUser, selectIsQaUser } from '../../auth/authSlice';

export interface FilterField {
    type: 'text' | 'date' | 'merchant' | 'garmentType' | 'categoryTags' | 'sessionTags' | 'icon';
    name: string;
    label?: string;
    value?: string | string[] | moment.Moment | null;
    onChange: ((event: any) => void);
    onBlur?: () => void;
    onClick?: () => void;
    icon?: React.ReactNode;
    merchantId?: number | undefined;
    access?: 'boddOnly' | 'boddQa' | 'all';
}

interface FilterComponentProps {
    fields: FilterField[];
    countFilteredItems: () => number;
    clearFilter?: () => void;
    exportCsv?: () => void;
}

const FilterComponent: FC<FilterComponentProps> = (props) => {
    const theme = useTheme();
    const { fields, countFilteredItems, clearFilter, exportCsv } = props;
    const filteredItemsCount = countFilteredItems();
    const isActive = filteredItemsCount > 0;
    const scrollRef = useRef<HTMLDivElement>(null);
    const leftGRadientRef = useRef<HTMLDivElement>(null);
    const [showLeftGradient, setShowLeftGradient] = useState(false);
    const [showRightGradient, setShowRightGradient] = useState(false);
    const isLargeLayout = useMediaQuery(theme.breakpoints.up('lg'));
    const isBoddUser = useAppSelector(selectIsBoddUser);
    const isQaUser = useAppSelector(selectIsQaUser);
    const isBoddOrQaUser = isBoddUser || isQaUser;

    const availableFields = fields
        .filter((f) => f.access === undefined || f.access === 'all' || (isBoddUser && f.access === 'boddOnly') || (isBoddOrQaUser && f.access === 'boddQa'));

    const checkScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setShowLeftGradient(scrollLeft > 0);
            setShowRightGradient(scrollLeft + clientWidth < scrollWidth);
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, []);

    return (
        <div
            className={styles.FilterComponent}
            data-testid="FilterComponent"
            style={{ position: 'relative', width: '100%' }}
        >
            <Stack
                direction="row"
                sx={{
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        borderRadius: '4px',
                        backgroundColor: isActive ? boddVariables.grey600 : boddVariables.grey10,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 1,
                    }}
                >
                    <FilterListIcon sx={{ color: isActive ? boddVariables.white : boddVariables.grey600 }} />
                    {isActive && (
                        <Typography
                            sx={{
                                color: boddVariables.white,
                                fontSize: '16px',
                                fontWeight: 600,
                                ml: '10px',
                            }}
                        >
                            {filteredItemsCount}
                        </Typography>
                    )}
                </Box>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ justifyContent: 'flex-end' }}
                >
                    {exportCsv && (
                        <Button
                            variant="text"
                            onClick={exportCsv}
                            startIcon={<SpreadsheetIcon />}
                        >
                            Export CSV
                        </Button>

                    )}
                    {clearFilter && (
                        <Button
                            variant="text"
                            onClick={clearFilter}
                            startIcon={<ClearIcon />}
                        >
                            Clear
                        </Button>
                    )}
                </Stack>
            </Stack>
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                    pt: '16px',
                    pb: 2,
                    overflowX: {
                        xs: 'scroll',
                        lg: 'auto',
                    },
                    ...(isLargeLayout ? {} : {
                        '&::-webkit-scrollbar': {
                            display: 'none', // hide scrollbar for WebKit browsers
                        },
                        msOverflowStyle: 'none', // hide scrollbar for IE and Edge
                        scrollbarWidth: 'none', // hide scrollbar for Firefox
                    }),
                }}
                ref={scrollRef}
                onScroll={checkScroll}
            >
                {availableFields.map((field) => (
                    <div key={field.name} style={{ display: 'inline-block' }}>
                        {field.type === 'text' && (
                            (field.access === 'boddOnly' || field.access === 'boddQa') ? (
                                <Box
                                    sx={{ width: '200px' }}
                                    className={`${styles.BoddOnlyComponent}`}
                                >
                                    <TextFilterComponent
                                        value={field.value as string}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        key={field.name}
                                        name={field.name}
                                        label={field.label}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ width: '200px' }}>
                                    <TextFilterComponent
                                        value={field.value as string}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        key={field.name}
                                        name={field.name}
                                        label={field.label}
                                    />
                                </Box>
                            )
                        )}
                        {field.type === 'date' && (
                            (field.access === 'boddOnly' || field.access === 'boddQa') ? (
                                <DatePicker
                                    className={`${styles.BoddOnlyComponent}`}
                                    onChange={(val) => field.onChange({ target: { name: field.name, value: val } })}
                                    label={field.label}
                                    value={field.value as moment.Moment | null}
                                    sx={{ width: '200px' }}
                                />
                            ) : (
                                <DatePicker
                                    onChange={(val) => field.onChange({ target: { name: field.name, value: val } })}
                                    label={field.label}
                                    value={field.value as moment.Moment | null}
                                    sx={{ width: '200px' }}
                                />
                            )
                        )}
                        {field.type === 'merchant' && (
                            (field.access === 'boddOnly' || field.access === 'boddQa') ? (
                                <Box sx={{ width: '200px' }} className={`${styles.BoddOnlyComponent}`}>
                                    <MerchantSelectionComponent
                                        onChange={(m) => field.onChange({
                                            target: {
                                                name: field.name,
                                                value: m?.id ?? null,
                                            },
                                        })}
                                        selectedMerchantId={Number(field.value ?? 0)}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ width: '200px' }}>
                                    <MerchantSelectionComponent
                                        onChange={(m) => field.onChange({
                                            target: {
                                                name: field.name,
                                                value: m?.id ?? null,
                                            },
                                        })}
                                        selectedMerchantId={Number(field.value ?? 0)}
                                    />
                                </Box>
                            )
                        )}
                        {field.type === 'icon' && (
                            (field.access === 'boddOnly' || field.access === 'boddQa') ? (
                                <Box className={`${styles.BoddOnlyComponent}`}>
                                    <IconButton onClick={field.onClick}>
                                        {field.icon}
                                    </IconButton>
                                </Box>
                            ) : (
                                <IconButton onClick={field.onClick}>
                                    {field.icon}
                                </IconButton>
                            )
                        )}
                        {field.type === 'garmentType' && (
                            (field.access === 'boddOnly' || field.access === 'boddQa') ? (
                                <Box sx={{ width: '200px' }} className={`${styles.BoddOnlyComponent}`}>
                                    <GarmentTypeFilterComponent
                                        value={field.value as string}
                                        onBlur={field.onBlur ?? (() => null)}
                                        onChange={field.onChange}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ width: '200px' }}>
                                    <GarmentTypeFilterComponent
                                        value={field.value as string}
                                        onBlur={field.onBlur ?? (() => null)}
                                        onChange={field.onChange}
                                    />
                                </Box>
                            )
                        )}
                        {field.type === 'categoryTags' && (
                            (field.access === 'boddOnly' || field.access === 'boddQa') ? (
                                <Box sx={{ width: '200px' }} className={`${styles.BoddOnlyComponent}`}>
                                    <CategoryTagFilterComponent
                                        value={field.value as string}
                                        onChange={field.onChange}
                                        merchantId={field.merchantId}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ width: '200px' }}>
                                    <CategoryTagFilterComponent
                                        value={field.value as string}
                                        onChange={field.onChange}
                                        merchantId={field.merchantId}
                                    />
                                </Box>
                            )
                        )}
                        {field.type === 'sessionTags' && (
                            <Box sx={{ width: '200px' }} className={`${styles.BoddOnlyComponent}`}>
                                <SessionTagFilterComponent
                                    value={field.value as string}
                                    onChange={field.onChange}
                                />
                            </Box>
                        )}
                    </div>
                ))}
            </Stack>
            {showLeftGradient && (
                <div
                    ref={leftGRadientRef}
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: (scrollRef.current?.offsetTop ?? 0) + 16,
                        width: '16px',
                        height: '40px',
                        background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 90.62%)',
                        pointerEvents: 'none',
                    }}
                />
            )}
            {showRightGradient && (
                <div
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: (scrollRef.current?.offsetTop ?? 0) + 16,
                        width: '16px',
                        height: '40px',
                        background: 'linear-gradient(270deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 90.62%)',
                        pointerEvents: 'none',
                    }}
                />
            )}
        </div>
    );
};

FilterComponent.defaultProps = {
    clearFilter: undefined,
    exportCsv: undefined,
};

export default FilterComponent;
